/**
 * Outbox v2 — putting a master write into the queue (ATL-1445 stage 3, P3; plan §1 steps 1 and §2).
 *
 * Two steps, split on purpose (P2 obligation 1):
 *
 *   1. `prepareQueuedWrite(input)` — synchronous, runs inside the writer's `planX`, BEFORE the cart
 *      is cleared. It mints the entryId and applies the exact validation the store will apply
 *      (`prepareNewEntry`), so a write the store would refuse fails while the cashier can still see
 *      it. Its result is frozen: the writer cannot change the entry between plan and queue.
 *   2. `enqueuePrepared(store, prepared)` — persists it. Resolves only once the store reports the
 *      entry durable.
 *
 * What each outcome of step 2 means (P2 obligations 4, 5, 7):
 *
 *  - resolves `{ status: 'queued' }` — the entry is on disk, exactly once. For a delete, `purged`,
 *    `purgeWithheld` and `purgeIncomplete` are reported to the alert sink (none of them is an error:
 *    see `AppendDeleteResult`).
 *  - resolves `{ status: 'already-queued' }` — a duplicate refusal with `existing === 'ok'`: an
 *    earlier attempt under the SAME entryId landed. Only that shape counts as landed.
 *  - rejects `OutboxEnqueueConflictError` — the entryId is held by a record the store cannot read.
 *    The write is NOT recoverable from the store; the caller keeps its copy and it is surfaced.
 *  - rejects `OutboxEnqueueIndeterminateError` — the store could not tell whether the entry was
 *    queued, even after one retry under the same entryId. The caller MUST NOT re-issue the write
 *    under a fresh id (that books it twice); a restart's `get(entryId)` answers.
 *  - any other rejection — nothing was queued.
 *
 * The retry after an indeterminate append reuses the prepared entry verbatim, so it carries the same
 * entryId; that is the whole of what makes it safe.
 */
import type { OutboxAlertSink } from './outboxAlerts'
import { v1MigrationSettled } from './v1MigrationBarrier'
import { getSessionWriteModePin, type SessionWriteModePin, type WOperation, type WPlan } from './writeW'
import {
  isStorageSafeId,
  OUTBOX_ENTRY_VERSION,
  prepareNewEntry,
  type JsonValue,
  type NewOutboxEntry,
  type OutboxEntryV2,
  type OutboxRequest,
  type OutboxWriteKind,
} from './outboxEntry'
import {
  OutboxAppendIndeterminateError,
  OutboxDuplicateEntryError,
  OutboxValidationError,
  type OutboxStore,
  type PurgeWithheldReason,
} from './outboxStore'

/** What a writer supplies. Everything else is minted or defaulted here. */
export interface QueuedWriteInput {
  orgFirestoreId: string
  /** `module.op`, the same label today's dual-write uses. */
  label: string
  /** Device-minted Firestore id: the backend's idempotency key (invariant 13). */
  documentId: string
  relatedIds?: readonly string[]
  kind: OutboxWriteKind
  request: OutboxRequest
  effects?: JsonValue | null
  /** The device's checkout time (invariant 7). */
  enqueuedAt: number
  bundleId: string | null
}

// ============================================================================
// The seam's operation → a queued write (P4 × P3)
// ============================================================================

/** The `WOperation` fields that are the seam's own business and are never persisted. */
export type SeamOnlyOpField = 'offline' | 'firestoreFirst' | 'plan'

/**
 * The `WPlan` fields that stay with the seam (P9: the plan is lazy, `writeW.ts` decision 1): what the
 * writer returns on the ack, how a refuse-offline write reads its answer (review 1, S2-b and S3-3), and the
 * side effect a queued write runs just before its append (review 1, S2-c) — whose result is IN the
 * persisted request, so the step itself is never persisted.
 */
export type SeamOnlyPlanField = 'fromAck' | 'ackDisposition' | 'fromAbsent' | 'beforeAppend'

/**
 * Every OTHER field of the operation and of its plan — DERIVED, not listed (final check B1): a field
 * added to `WOperation` or `WPlan` joins this set by construction, and then fails to compile below
 * until it is mapped.
 */
export type PersistedOpField = Exclude<keyof WOperation<unknown>, SeamOnlyOpField> | Exclude<keyof WPlan<unknown>, SeamOnlyPlanField>

/**
 * What the seam hands the queue (P9): the operation's own persisted fields and its plan's, plus
 * `offline` so the refusal of invariant 12 is checked here too.
 */
export type PlannedOperation = Pick<WOperation<unknown>, Exclude<keyof WOperation<unknown>, SeamOnlyOpField> | 'offline'> &
  Omit<WPlan<unknown>, SeamOnlyPlanField>

/** The fields of a queued write that come from the SESSION, not from the operation. */
export type SessionQueuedField = 'orgFirestoreId' | 'enqueuedAt' | 'bundleId'

type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false

/**
 * Type-level proof that the operation's persisted fields are EXACTLY the queued write's non-session
 * fields: a field on one side and not the other is a compile error here (TS2322).
 */
export const OP_FIELDS_ARE_ENTRY_FIELDS: Equals<PersistedOpField, Exclude<keyof QueuedWriteInput, SessionQueuedField>> = true

/**
 * Exhaustive over `PersistedOpField` (a missing key is TS2741, an extra one TS2353). The tests iterate
 * it, so a field the function body below forgets to copy — an OPTIONAL one compiles either way — fails.
 */
export const PERSISTED_OP_FIELDS: { readonly [K in PersistedOpField]: true } = Object.freeze({
  label: true,
  documentId: true,
  relatedIds: true,
  kind: true,
  request: true,
  effects: true,
})

/** An operation the queue may hold: only an `offline: 'queue'` one (invariant 12). */
export type QueueableOperation = Pick<PlannedOperation, PersistedOpField> & { offline: 'queue' }

export type NotQueueableReason = 'offline-refuse' | 'no-pin' | 'stale-pin' | 'session-not-supabase'

/** The operation may not be queued at all; nothing was queued. */
export class OutboxNotQueueableError extends Error {
  constructor(
    public readonly reason: NotQueueableReason,
    public readonly label: string,
  ) {
    super(`outbox v2 refuses to queue ${label}: ${reason}`)
    this.name = 'OutboxNotQueueableError'
  }
}

/**
 * How the seam's operation becomes a queued write. P4's `writeW` receives a `WOperation`; its
 * backend-first branch (P9, `writeWBackendFirst.ts`) runs the operation's plan and queues the pair
 * (`PlannedOperation`), and this is the ONE mapping it may use.
 *  - The persisted fields are the operation's and its plan's own, verbatim (`PERSISTED_OP_FIELDS`; the
 *    type checker holds `WOperation`, `WPlan` and `QueuedWriteInput` together, see
 *    `OP_FIELDS_ARE_ENTRY_FIELDS`).
 *  - The ORGANIZATION is the session pin's (`pin.decision.organizationId`), never a free-form value:
 *    an operation is queued only under the organization whose pin put the session in Supabase mode.
 *    The pin passed in must be the one in force now (`getSessionWriteModePin()`), so a pin captured
 *    before a `replaced-organization` re-pin (sign-out into another org) is refused as `stale-pin`.
 *  - `offline: 'refuse'` operations are never queued (invariant 12, stage-0 baseline §4.2: money-
 *    moving deletes fail visibly offline and apply nothing). The type admits only `'queue'`; the
 *    runtime check backs it for `.js` callers, which the type does not reach.
 *  - The checkout time and the bundle are the session's. `firestoreFirst`, `plan` and the plan's
 *    `fromAck` stay with the seam.
 * Throws `OutboxNotQueueableError` — synchronously, before anything is written.
 */
export function queuedWriteInputOf(
  op: QueueableOperation,
  session: { pin: SessionWriteModePin | null; enqueuedAt: number; bundleId: string | null },
): QueuedWriteInput {
  if ((op as { offline?: unknown }).offline !== 'queue') throw new OutboxNotQueueableError('offline-refuse', op.label)
  const pin = session.pin
  if (!pin) throw new OutboxNotQueueableError('no-pin', op.label)
  if (pin !== getSessionWriteModePin()) throw new OutboxNotQueueableError('stale-pin', op.label)
  if (pin.decision.mode !== 'supabase') throw new OutboxNotQueueableError('session-not-supabase', op.label)
  return {
    orgFirestoreId: pin.decision.organizationId,
    label: op.label,
    documentId: op.documentId,
    relatedIds: op.relatedIds,
    kind: op.kind,
    request: op.request,
    effects: op.effects ?? null,
    enqueuedAt: session.enqueuedAt,
    bundleId: session.bundleId,
  }
}

declare const preparedBrand: unique symbol

/** A validated, frozen entry. Only `prepareQueuedWrite` makes one. */
export type PreparedWrite = Readonly<{ entry: Readonly<NewOutboxEntry>; [preparedBrand]: true }>

/**
 * A fresh entryId: storage-safe, unique per device. Not a secret, so no crypto module is assumed
 * (Hermes has no `crypto.randomUUID`): clock prefix plus ~78 random bits.
 */
export function generateEntryId(random: () => number = Math.random, now: () => number = Date.now): string {
  let tail = ''
  for (let i = 0; i < 3; i++) tail += Math.floor(random() * 2 ** 26).toString(36).padStart(6, '0')
  return `w-${now().toString(36)}-${tail}`
}

/**
 * Step 1. Throws `OutboxValidationError` — synchronously, before any I/O — when the store would
 * refuse the entry. Call it before the cart is cleared.
 */
export function prepareQueuedWrite(
  input: QueuedWriteInput,
  options: { entryId?: () => string } = {},
): PreparedWrite {
  const entryId = (options.entryId ?? generateEntryId)()
  if (!isStorageSafeId(entryId)) throw new OutboxValidationError('entryId: not a storage-safe id')
  const candidate: NewOutboxEntry = {
    v: OUTBOX_ENTRY_VERSION,
    entryId,
    orgFirestoreId: input.orgFirestoreId,
    label: input.label,
    documentId: input.documentId,
    relatedIds: [...(input.relatedIds ?? [])],
    kind: input.kind,
    request: input.request,
    effects: input.effects ?? null,
    enqueuedAt: input.enqueuedAt,
    bundleId: input.bundleId,
    origin: 'live',
    state: 'pending',
    attempts: 0,
    lastError: null,
    heldSince: null,
    nextAttemptAt: 0,
  }
  // The store's own rules, applied now. A delete sent as anything but DELETE is refused here too.
  const prepared = prepareNewEntry(candidate)
  if (!prepared.ok) throw new OutboxValidationError(prepared.reason)
  return Object.freeze({ entry: deepFreeze(prepared.value) }) as PreparedWrite
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child)
    Object.freeze(value)
  }
  return value
}

export type EnqueueResult =
  | {
      status: 'queued'
      entry: OutboxEntryV2
      purged: OutboxEntryV2[]
      purgeWithheld: PurgeWithheldReason | null
      purgeIncomplete: boolean
    }
  | { status: 'already-queued'; entryId: string }

/** The entryId is held by an unreadable record: this write is not recoverable from the store. */
export class OutboxEnqueueConflictError extends Error {
  constructor(
    public readonly orgFirestoreId: string,
    public readonly entryId: string,
  ) {
    super(`outbox entry ${entryId} collides with an unreadable record for organization ${orgFirestoreId}`)
    this.name = 'OutboxEnqueueConflictError'
  }
}

/** Maybe queued. Never re-issue under another entryId; this one is the dedupe key. */
export class OutboxEnqueueIndeterminateError extends Error {
  constructor(
    public readonly orgFirestoreId: string,
    public readonly entryId: string,
    public readonly cause: unknown,
  ) {
    super(`outbox enqueue of ${entryId} for organization ${orgFirestoreId} has an unknown outcome`)
    this.name = 'OutboxEnqueueIndeterminateError'
  }
}

async function appendOnce(store: OutboxStore, entry: NewOutboxEntry): Promise<EnqueueResult> {
  if (entry.kind === 'delete') {
    const result = await store.appendDelete(entry)
    return { status: 'queued', ...result }
  }
  return { status: 'queued', entry: await store.append(entry), purged: [], purgeWithheld: null, purgeIncomplete: false }
}

/**
 * Step 2. `store` is the one `openOutboxStore()` returns NOW — callers never keep it across a
 * `close()` (P2 obligation 8).
 */
export async function enqueuePrepared(
  store: OutboxStore,
  prepared: PreparedWrite,
  sink: Pick<OutboxAlertSink, 'deletePurge' | 'enqueueUnresolved'>,
): Promise<EnqueueResult> {
  // P8: a v1 → v2 migration of this organization, if one is running in this JS context, goes first —
  // that is what puts the migrated (pre-flip) writes at the HEAD of the queue, ahead of every live
  // (post-flip) one (`v1MigrationBarrier.ts`). Resolves at once when none runs; never rejects.
  await v1MigrationSettled(prepared.entry.orgFirestoreId)
  // A fresh, unfrozen copy: the engines are free to spread it, and the prepared one stays pristine.
  const entry = JSON.parse(JSON.stringify(prepared.entry)) as NewOutboxEntry
  let result: EnqueueResult
  try {
    result = await appendResolvingDuplicate(store, entry)
  } catch (error) {
    if (!(error instanceof OutboxAppendIndeterminateError)) throw error
    // One retry under the SAME entryId (P2 obligation 4). A duplicate refusal with `ok` means the
    // first attempt did land.
    try {
      result = await appendResolvingDuplicate(store, entry)
    } catch (second) {
      if (second instanceof OutboxAppendIndeterminateError) {
        sink.enqueueUnresolved({ label: entry.label, kind: 'indeterminate' })
        throw new OutboxEnqueueIndeterminateError(entry.orgFirestoreId, entry.entryId, second)
      }
      throw second
    }
  }
  if (result.status === 'queued' && entry.kind === 'delete' && (result.purged.length > 0 || result.purgeWithheld || result.purgeIncomplete)) {
    sink.deletePurge({
      label: entry.label,
      purged: result.purged.length,
      purgeWithheld: result.purgeWithheld,
      purgeIncomplete: result.purgeIncomplete,
    })
  }
  return result

  async function appendResolvingDuplicate(s: OutboxStore, e: NewOutboxEntry): Promise<EnqueueResult> {
    try {
      return await appendOnce(s, e)
    } catch (error) {
      if (error instanceof OutboxDuplicateEntryError) {
        // P2 obligation 5: landed ONLY when the record holding the id is readable.
        if (error.existing === 'ok') return { status: 'already-queued', entryId: e.entryId }
        sink.enqueueUnresolved({ label: e.label, kind: 'corrupt-duplicate' })
        throw new OutboxEnqueueConflictError(e.orgFirestoreId, e.entryId)
      }
      throw error
    }
  }
}
